<template>
  <div
    :id="`meeting-${meeting.id}`"
    ref="meetingCard"
    class="
      tw-border
      tw-bg-sf-snow-lighter
      tw-border-sf-neutral-lighter-2
      tw-shadow-sm
      tw-rounded-lg
    "
    data-testid="past-meeting-card"
  >
    <header
      class="
        tw-flex
        tw-h-[60px]
        tw-items-center
        tw-justify-between
        tw-bg-sf-snow-light
        tw-rounded-t-lg
        tw-px-6
      "
      :class="{
        'tw-rounded-b-lg': !meeting.summary,
      }"
    >
      <div class="tw-flex tw-items-center tw-gap-4">
        <span
          class="tw-flex tw-items-center tw-cursor-pointer tw-w-8 tw-h-8"
          data-testid="expand-meeting-button"
          :class="{ 'tw-cursor-not-allowed tw-opacity-50': hasPendingProposal }"
          @click="hasPendingProposal ? null : (isExpanded = !isExpanded)"
        >
          <i
            :class="isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"
            class="fa tw-text-sf-neutral-light-2 tw-mx-auto"
          ></i>
        </span>
        <span
          v-if="meeting.title"
          class="tw-font-medium tw-text-base tw-text-sf-neutral-dark-2"
        >
          {{ meeting.title }}
        </span>
        <span class="tw-flex tw-items-center tw-gap-2">
          <i class="fa fa-calendar tw-text-sf-neutral-light-2"></i>
          <span class="tw-text-sm tw-text-sf-neutral-dark-1">
            {{ meeting.completedAt }}
          </span>
        </span>
      </div>
      <div class="tw-flex tw-items-center">
        <template v-if="!(hasPendingProposal || hasProcessingProposal)">
          <SfButtonDs
            v-if="!exportInProgress"
            variant="tertiary"
            data-heap="talking-points-export-past"
            @click="generateExportHandler"
          >
            <i class="fa fa-download"></i>
            <span>{{ copy.exportButton }}</span>
          </SfButtonDs>
          <div v-else class="tw-flex tw-items-center tw-gap-2 tw-p-1 tw-pl-2">
            <span class="sf-spinner"></span>
            <span>{{ copy.generatingExport }}</span>
          </div>
        </template>
        <span
          class="
            tw-flex
            tw-items-center
            tw-cursor-pointer
            tw-w-8
            tw-h-8
            tw-rounded-md
            hover:tw-bg-sf-snow-dark
            active:tw-bg-sf-snow-darker
          "
          data-testid="delete-meeting-button"
          @click="openDeleteMeetingModal(meeting)"
        >
          <i class="fa fa-trash tw-text-sf-neutral-light-2 tw-mx-auto"></i>
        </span>
        <MeetingProposalActions
          v-if="hasPendingProposal"
          :proposal-id="meeting.proposalId!"
        />
      </div>
    </header>
    <div
      v-if="isExpanded || meeting.summary"
      class="tw-flex tw-flex-col tw-gap-4 tw-px-8 tw-py-4"
    >
      <div
        v-if="!isExpanded && meeting.summary"
        class="
          tw-bg-slate-50
          tw-rounded-lg
          tw-p-6
          tw-pt-4
          tw-text-sf-neutral-darker-1
          tw-flex
          tw-flex-col
          tw-gap-4
        "
      >
        <div v-dompurify-html="htmlSummary[meeting.id]"></div>
      </div>

      <div v-if="isExpanded" class="tw-flex tw-flex-col tw-gap-4 tw-relative">
        <template v-if="loading">
          <RemarkSkeleton v-for="i in 2" :key="i" />
        </template>
        <MeetingProposalLoader v-else-if="hasProcessingProposal" />
        <MeetingProposal v-else-if="hasPendingProposal" :meeting="meeting" />
        <MeetingDetails v-else :meeting="meeting" />
        <MeetingProposalFinalizingOverlay v-if="isFinalizingProposal" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, onMounted, useTemplateRef, watch, nextTick } from 'vue'

import { useStore } from 'vuex'
import { storeToRefs } from 'pinia'

import useI18n from '@app/shared/vue/composables/use-I18n'
import RemarkSkeleton from '@app/shared/vue/components/molecules/remark/remark-skeleton.vue'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import { getHeaderOffset } from '@app/application/behaviors/fixed-table-headers/utils'
import { isUsingNewLayout } from '@app/adapters/silverfin'

import useExport from '@app/application/client_meetings/screens/composables/use-export'
import type { Remark } from '@app/application/communication/types'
import type { Meeting } from '@app/application/client_meetings/types'
import { ProposalStatus } from '@app/application/client_meetings/types'
import MeetingProposalLoader from '@app/application/client_meetings/screens/past/components/meeting-proposal/meeting-proposal-loader.vue'
import MeetingProposal from '@app/application/client_meetings/screens/past/components/meeting-proposal/meeting-proposal.vue'
import MeetingProposalActions from '@app/application/client_meetings/screens/past/components/meeting-proposal/meeting-proposal-actions.vue'
import MeetingProposalFinalizingOverlay from '@app/application/client_meetings/screens/past/components/meeting-proposal/meeting-proposal-finalizing-overlay.vue'
import { useProposalDetailsStore } from '@app/application/client_meetings/screens/past/stores/proposal'
import { usePastMeetingsStore } from '@app/application/client_meetings/screens/past/stores/past-meetings'
import MeetingDetails from '@app/application/client_meetings/screens/past/components/meeting-details.vue'

const store = useStore()

const I18n = useI18n()
const copy = {
  exportButton: I18n.t('client_meetings.exports.download_processed'),
  generatingExport: I18n.t('client_meetings.exports.generating'),
}

interface Props {
  meeting: Meeting
  latestMeeting: boolean
}

const props = defineProps<Props>()

const proposalStore = useProposalDetailsStore()
const { statusByProposalId, isFinalizingProposal } = storeToRefs(proposalStore)
const pastMeetingsStore = usePastMeetingsStore()
const { htmlSummary } = storeToRefs(pastMeetingsStore)

const proposalStatus = computed(() =>
  props.meeting.proposalId
    ? statusByProposalId.value?.[props.meeting.proposalId]
    : undefined
)

const isExpanded = ref(props.latestMeeting)
const meetingCardRef = useTemplateRef('meetingCard')
const usingNewLayout = isUsingNewLayout()

const remarkIds = computed(() => {
  const remarks = Object.values(
    store.state.remarks.remarks as Record<string, Remark>
  )

  return remarks
    .filter((remark) => remark.clientMeetingId === props.meeting.id)
    .map((remark) => remark.id)
})

const empty = computed(() => remarkIds.value.length === 0)

const loading = computed(
  () => empty.value && store.getters.fetchingCommunicationsInProgress
)

const hasPendingProposal = computed(() => {
  return proposalStatus.value === ProposalStatus.Pending
})

// Check if last proposal is still processing
const hasProcessingProposal = computed(() => {
  return proposalStatus.value === ProposalStatus.Generating
})

const includeHashRemark = (id: string) => {
  if (id.startsWith('#remark-')) {
    const remarkId = Number(id.replace('#remark-', ''))
    return remarkIds.value.some((id: number) => id === remarkId)
  }
  return false
}

watch(remarkIds, () => {
  const elementId = window.location.hash
  if (includeHashRemark(elementId)) {
    isExpanded.value = true
  }
})

onMounted(() => {
  if (
    window.location.hash !== '' &&
    window.location.hash !== `#meeting-${props.meeting.id}`
  ) {
    isExpanded.value = false
  }

  if (
    hasProcessingProposal.value ||
    hasPendingProposal.value ||
    window.location.hash === `#meeting-${props.meeting.id}`
  ) {
    isExpanded.value = true
  }
})

watch(isExpanded, (newValue, oldValue) => {
  nextTick(() => {
    if (newValue && newValue !== oldValue && meetingCardRef.value) {
      if (usingNewLayout) {
        meetingCardRef.value.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      } else {
        window.scrollTo({
          top:
            meetingCardRef.value.getBoundingClientRect().top +
            window.scrollY -
            getHeaderOffset(),
          behavior: 'smooth',
        })
      }
    }
  })
})

function openDeleteMeetingModal(meeting: Meeting) {
  window.modalCitizen.openModalPortal('delete-meeting-modal', { meeting })
}

const { exportInProgress, generateExport } = useExport({
  meetingId: props.meeting.id,
})

async function generateExportHandler() {
  const response = await generateExport()
  if (response && response.url) {
    window.open(response.url, '_blank')
  }
}
</script>
